import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { fetchArticleBySlug, ARTICLE_CATEGORY_LABELS } from "@/lib/articles.functions";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { SmartImage } from "@/components/smart-image";
import { useTranslation } from "@/i18n";

const artOptions = (slug: string) =>
  queryOptions({
    queryKey: ["community-article", slug],
    queryFn: async () => {
      const a = await fetchArticleBySlug(slug);
      if (!a || a.status !== "published") throw notFound();
      return a;
    },
  });

export const Route = createFileRoute("/community/articles/$slug")({
  loader: ({ params, context }) => context.queryClient.ensureQueryData(artOptions(params.slug)),
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Article unavailable — BidNova Auctions" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const a = loaderData;
    const url = `https://bidnovaauctions.uk/community/articles/${params.slug}`;
    const title = a.seo_title ?? `${a.title} — BidNova Community`;
    const desc =
      a.seo_description ??
      a.excerpt?.slice(0, 155) ??
      `${a.title} — a BidNova community article.`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: a.title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        ...(a.cover_image_url
          ? [
              { property: "og:image", content: a.cover_image_url },
              { name: "twitter:image", content: a.cover_image_url },
              { name: "twitter:card", content: "summary_large_image" },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: a.title,
            description: desc,
            image: a.cover_image_url ?? undefined,
            datePublished: a.published_at ?? a.created_at,
            dateModified: a.updated_at,
            author: a.author_name
              ? { "@type": "Person", name: a.author_name }
              : { "@type": "Organization", name: "BidNova Auctions" },
            publisher: {
              "@type": "Organization",
              name: "BidNova Auctions",
              url: "https://bidnovaauctions.uk",
            },
            mainEntityOfPage: url,
            keywords: a.tags?.join(", ") || undefined,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://bidnovaauctions.uk/" },
              { "@type": "ListItem", position: 2, name: "Community", item: "https://bidnovaauctions.uk/community" },
              { "@type": "ListItem", position: 3, name: "Articles", item: "https://bidnovaauctions.uk/community/articles" },
              { "@type": "ListItem", position: 4, name: a.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: ArticleDetail,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function ArticleDetail() {
  const { t } = useTranslation("community");
  const { slug } = Route.useParams();
  const { data: a } = useSuspenseQuery(artOptions(slug));

  return (
    <article className="mx-auto max-w-3xl px-6 sm:px-8 py-14">
      <nav aria-label="Breadcrumb" className="mb-6 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-primary">{t("layout.breadcrumbHome", "Home")}</Link>
        <span className="mx-2">/</span>
        <Link to="/community" className="hover:text-primary">{t("layout.breadcrumbCommunity", "Community")}</Link>
        <span className="mx-2">/</span>
        <Link to="/community/articles" className="hover:text-primary">{t("articles.index.title", "Articles")}</Link>
        <span className="mx-2">/</span>
        <span className="text-white/80 line-clamp-1">{a.title}</span>
      </nav>

      <p className="text-[11px] uppercase tracking-[0.24em] text-primary">
        {ARTICLE_CATEGORY_LABELS[a.category] ?? a.category}
      </p>
      <h1 className="mt-3 font-display text-3xl sm:text-4xl text-white">{a.title}</h1>
      <div className="mt-3 flex items-center gap-3 text-sm text-muted-foreground">
        <span>{a.author_name ?? t("articles.detail.authorFallback", "BidNova Team")}</span>
        {a.published_at && (
          <>
            <span aria-hidden>·</span>
            <time dateTime={a.published_at}>
              {new Date(a.published_at).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </time>
          </>
        )}
      </div>

      {a.cover_image_url && (
        <SmartImage
          src={a.cover_image_url}
          alt={`Cover image for ${a.title}`}
          width={1024}
          height={576}
          widths={[640, 960, 1280]}
          sizes="(max-width: 768px) 100vw, 768px"
          priority
          className="mt-8 w-full rounded-3xl border border-white/10 object-cover aspect-[16/9]"
        />
      )}

      {a.excerpt && (
        <p className="mt-8 text-lg text-white/85 leading-relaxed">{a.excerpt}</p>
      )}

      {a.content && (
        <div className="mt-6 whitespace-pre-wrap text-white/90 leading-relaxed">
          {a.content}
        </div>
      )}

      {a.tags?.length > 0 && (
        <div className="mt-10 flex flex-wrap gap-2">
          {a.tags.map((t) => (
            <span
              key={t}
              className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-muted-foreground"
            >
              #{t}
            </span>
          ))}
        </div>
      )}

      <div className="mt-12">
        <Link
          to="/community/articles"
          className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> {t("articles.detail.backLink", "Back to all articles")}
        </Link>
      </div>
    </article>
  );
}
